import React, { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./editFranchiseManager.css";
import {
  editFranchiseManager,
  getFranchiseManagerDetail,
  getFranchiseManagerProjectList,
  getFranchiseManagerLocalityList,
  getFranchiseManagerCityList,
} from "../../Redux/Actions/franchiseManagerAction";
import { useDispatch, useSelector } from "react-redux";
import {
  viewCountryListPagination,
} from "../../Redux/Actions/settingsActions";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import SearchableMultiSelect from "../../Global Functions/SearchableMultiSelect";

const FRANCHISE_MANAGER_ROLE = "franchise manager";

const EditMasterFranchise = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const logoInputRef = useRef(null);

  const { countryList } = useSelector((state) => state.country);

  const {
    projectList: rawProjectList,
    localityList: rawLocalityList,
    cityList: rawCityList,
    franchiseManagerDetail,
    detailLoading = false,
    loading = false,
  } = useSelector((state) => state.franchiseManager || {});

  const projectList = Array.isArray(rawProjectList) ? rawProjectList : [];
  const localityGroups = Array.isArray(rawLocalityList) ? rawLocalityList : [];
  const cities = Array.isArray(rawCityList) ? rawCityList : [];

  const [activeSection, setActiveSection] = useState(0);
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoDragOver, setLogoDragOver] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const [franchiseData, setFranchiseData] = useState({
    name: "",
    email: "",
    country_code: "+91",
    contact_number: "",
    department: "",
    designation: "",
    country: "",
    city: [],
    locality: [],
    project: [],
    address: "",
    timezone: "Asia/Kolkata",
    profile_image: "",
  });

  // Flatten + dedupe localities across all currently-selected cities.
  // disabled = taken by someone else (not available AND not already mine)
  const localityOptions = useMemo(() => {
    const opts = [];
    localityGroups.forEach((group) => {
      if (franchiseData.city.includes(group?.city?._id)) {
        (group.localities || []).forEach((loc) => {
          opts.push({
            value: loc._id,
            label: loc.name,
            group: group.city?.name,
            hint: loc.is_major ? "Major" : undefined,
            disabled: loc.available === false && !loc.assigned_to_me,
          });
        });
      }
    });
    return opts;
  }, [localityGroups, franchiseData.city]);

  // Projects grouped by city name, business type shown as a hint badge
  const projectOptions = useMemo(() => {
    return projectList
      .filter((p) => {
        if (franchiseData.locality.length === 0) return true;
        const projLocalityIds = (p.localities || []).map((l) => l._id || l);
        if (projLocalityIds.length === 0) return false; // hide projects with no locality
        return projLocalityIds.some((lid) => franchiseData.locality.includes(lid));
      })
      .map((p) => ({
        value: p._id,
        label: p.project_name,
        group: p.city?.city_name,
        hint: p.business_type,
        disabled: p.available === false && !p.assigned_to_me,
      }));
  }, [projectList, franchiseData.locality]);

  useEffect(() => {
    dispatch(viewCountryListPagination());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      setHydrated(false);
      dispatch(getFranchiseManagerDetail(id));
    }
  }, [id, dispatch]);

  // Hydrate form once the detail record arrives
  useEffect(() => {
    if (!franchiseManagerDetail || hydrated || franchiseManagerDetail._id !== id) return;

    const detail = franchiseManagerDetail;
    const countryId = Array.isArray(detail.country) ? detail.country[0]?._id || detail.country[0] : detail.country?._id || detail.country;

    setFranchiseData({
      name: detail.name || "",
      email: detail.email || "",
      country_code: detail.country_code || "+91",
      contact_number: detail.contact_number || "",
      department: detail.department || "",
      designation: detail.designation || "",
      country: countryId || "",
      city: (detail.city || []).map((c) => c?._id || c),
      locality: (detail.locality || []).map((l) => l?._id || l),
      project: (detail.project || []).map((p) => p?._id || p),
      address: detail.address || "",
      timezone: detail.timezone || "Asia/Kolkata",
      profile_image: detail.profile_image || "",
    });

    if (detail.profile_image) setLogoPreview(detail.profile_image);
    setHydrated(true);
  }, [franchiseManagerDetail, hydrated]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (franchiseData.country !== "") {
        dispatch(getFranchiseManagerCityList({ country: franchiseData.country, franchisy_id: id }, false));
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [franchiseData.country, id, dispatch]);

  // Fetch localities whenever selected cities change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (franchiseData.city.length > 0) {
        dispatch(getFranchiseManagerLocalityList({ city: franchiseData.city, franchisy_id: id }, false));
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [franchiseData.city, id, dispatch]);

  // Fetch projects whenever selected cities or localities change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (franchiseData.city.length > 0) {
        dispatch(
          getFranchiseManagerProjectList(
            { city: franchiseData.city, franchisy_id: id },
            false
          )
        );
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [franchiseData.city, id, dispatch]);

  const clearError = (field) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleAddChange = (event) => {
    const { name, value } = event.target;

    if (name === "country") {
      setFranchiseData((prev) => ({
        ...prev,
        city: [],
        locality: [],
        project: [],
        country: value,
      }));
      clearError("country");
      return;
    }

    setFranchiseData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleCityChange = (values) => {
    setFranchiseData((prev) => {
      const keptLocality = prev.locality.filter((locId) =>
        localityGroups.some(
          (group) =>
            values.includes(group?.city?._id) &&
            (group.localities || []).some((loc) => loc._id === locId)
        )
      );

      const keptProject = prev.project.filter((projId) => {
        const proj = projectList.find((p) => p._id === projId);
        return proj && values.includes(proj.city?._id);
      });

      return {
        ...prev,
        city: values,
        locality: keptLocality,
        project: keptProject,
      };
    });
    clearError("city");
  };

  const handleLocalityChange = (values) => {
    setFranchiseData((prev) => {
      // A project is selectable if it's not disabled per the same rule used in projectOptions:
      // disabled = available === false && !assigned_to_me
      const isSelectable = (p) => !(p.available === false && !p.assigned_to_me);

      // Auto-select every selectable project that belongs to a newly selected locality
      const autoSelectedFromLocality = projectList
        .filter((p) => {
          if (!isSelectable(p)) return false;
          const projLocalityIds = (p.localities || []).map((l) => l._id || l);
          return projLocalityIds.some((lid) => values.includes(lid));
        })
        .map((p) => p._id);

      // Keep existing selections that are still valid:
      // - projects tied to specific localities stay only if that locality is still selected
      // - city-wide projects (no localities array) are left untouched here
      const keptProject = prev.project.filter((projId) => {
        const proj = projectList.find((p) => p._id === projId);
        if (!proj) return true; // not loaded yet (e.g. still hydrating) — don't drop it
        const projLocalityIds = (proj.localities || []).map((l) => l._id || l);
        if (projLocalityIds.length === 0) return false;
        return projLocalityIds.some((lid) => values.includes(lid));
      });

      const mergedProjects = Array.from(new Set([...keptProject, ...autoSelectedFromLocality]));

      return {
        ...prev,
        locality: values,
        project: values.length === 0 ? [] : mergedProjects,
      };
    });
    clearError("locality");
  };

  const handleProjectChange = (values) => {
    setFranchiseData((prev) => ({ ...prev, project: values }));
    clearError("project");
  };

  const processLogoFile = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);

    try {
      setUploadingLogo(true);
      const url = await AddImage(file);
      setFranchiseData((prev) => ({ ...prev, profile_image: url }));
      clearError("profile_image");
    } catch (error) {
      toast.error("Profile image upload failed");
      setLogoPreview(null);
      setFranchiseData((prev) => ({ ...prev, profile_image: "" }));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoFileChange = (e) => {
    processLogoFile(e.target.files?.[0]);
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    setLogoDragOver(false);
    processLogoFile(e.dataTransfer.files?.[0]);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFranchiseData((prev) => ({ ...prev, profile_image: "" }));
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const sections = ["Basic Info", "Location & Access"];

  const handleNextSection = () => {
    if (activeSection === 0) {
      const sectionErrors = {};

      if (!franchiseData.name.trim()) {
        sectionErrors.name = "Name is required";
      }

      if (!franchiseData.email.trim()) {
        sectionErrors.email = "Email is required";
      }

      if (!franchiseData.contact_number.trim()) {
        sectionErrors.contact_number = "Contact number is required";
      }

      if (Object.keys(sectionErrors).length) {
        setErrors((prev) => ({ ...prev, ...sectionErrors }));
        return;
      }
    }

    const next = Math.min(sections.length - 1, activeSection + 1);
    setActiveSection(next);
  };

  const validate = () => {
    const newErrors = {};

    if (!franchiseData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!franchiseData.email.trim()) {
      newErrors.email = "Email is required";
    }

    if (!franchiseData.contact_number.trim()) {
      newErrors.contact_number = "Contact number is required";
    }

    if (!franchiseData.country) {
      newErrors.country = "Country is required";
    }

    if (!franchiseData.city.length) {
      newErrors.city = "City is required";
    }

    if (!franchiseData.locality.length) {
      newErrors.locality = "At least one locality is required";
    }

    if (!franchiseData.project.length) {
      newErrors.project = "At least one project is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      if (!franchiseData.name.trim() || !franchiseData.email.trim() || !franchiseData.contact_number.trim()) {
        setActiveSection(0);
      } else if (
        !franchiseData.country ||
        !franchiseData.city.length ||
        !franchiseData.locality.length ||
        !franchiseData.project.length
      ) {
        setActiveSection(1);
      }
      return;
    }

    try {
      const payload = {
        id,
        name: franchiseData.name,
        email: franchiseData.email,
        country_code: franchiseData.country_code,
        contact_number: franchiseData.contact_number,
        role: FRANCHISE_MANAGER_ROLE,
        country: [franchiseData.country],
        department: franchiseData.department,
        designation: franchiseData.designation,
        address: franchiseData.address,
        timezone: franchiseData.timezone,
        city: franchiseData.city,
      };

      payload.locality = franchiseData.locality;
      payload.project = franchiseData.project;

      if (franchiseData.profile_image) {
        payload.profile_image = franchiseData.profile_image;
      }

      const response = await dispatch(editFranchiseManager(payload));

      if (response?.statusCode === 200) {
        toast.success("Master Franchise updated successfully!");
        await dispatch(getFranchiseManagerDetail(id));
        navigate("/franchise");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update master franchise";

      if (
        errorMessage.toLowerCase().includes("duplicate") ||
        errorMessage.toLowerCase().includes("already")
      ) {
        setErrors((prev) => ({
          ...prev,
          email: "Email already exists",
        }));
        setActiveSection(0);
        return toast.error("Email already exists");
      }

      toast.error(errorMessage);
    }
  };

  if (detailLoading && !hydrated) {
    return (
      <MainLayout>
        <div className="emf-wrapper">
          <div className="emf-loading-state">Loading franchise manager...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="emf-wrapper">
        <div className="emf-header">
          <div className="emf-header-left">
            <div className="emf-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              </svg>
            </div>
            <div>
              <h1 className="emf-title">Edit Master Franchise</h1>
              <p className="emf-subtitle">Update details for this master franchise manager</p>
            </div>
          </div>

          <Link to="/franchise" className="emf-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18" />
              <path d="M5 21V7l7-4 7 4v14" />
            </svg>
            View Franchise Managers
          </Link>
        </div>

        <div className="emf-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              className={`emf-nav-pill ${activeSection === i ? "emf-nav-pill--active" : ""}`}
              onClick={() => setActiveSection(i)}
              type="button"
              disabled={loading || uploadingLogo}
            >
              <span className="emf-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <form className="emf-form">
          <div className={`emf-section ${activeSection === 0 ? "emf-section--visible" : ""}`}>
            <div className="emf-section-header">
              <span className="emf-section-badge">01</span>
              <h2 className="emf-section-title">Basic Information</h2>
            </div>

            <div className="emf-grid emf-grid-2">
              <div className="emf-field">
                <label className="emf-label">
                  Name <span className="emf-req">*</span>
                </label>
                <input
                  className={`emf-input ${errors.name ? "emf-input--error" : ""}`}
                  type="text"
                  name="name"
                  value={franchiseData.name}
                  onChange={handleAddChange}
                  placeholder="e.g. Anita Verma"
                  disabled={loading}
                />
                {errors.name && <span className="emf-error-msg">{errors.name}</span>}
              </div>

              <div className="emf-field">
                <label className="emf-label">
                  Email <span className="emf-req">*</span>
                </label>
                <input
                  className={`emf-input ${errors.email ? "emf-input--error" : ""}`}
                  type="email"
                  name="email"
                  value={franchiseData.email}
                  onChange={handleAddChange}
                  placeholder="e.g. anita.verma@example.com"
                  disabled={loading}
                />
                {errors.email && <span className="emf-error-msg">{errors.email}</span>}
              </div>

              <div className="emf-field">
                <label className="emf-label">
                  Country Code <span className="emf-req">*</span>
                </label>
                <select
                  className="emf-select"
                  name="country_code"
                  value={franchiseData.country_code}
                  onChange={handleAddChange}
                  disabled={loading}
                >
                  <option value="+91">India (+91)</option>
                  <option value="+1">USA (+1)</option>
                  <option value="+971">UAE (+971)</option>
                </select>
              </div>

              <div className="emf-field">
                <label className="emf-label">
                  Contact Number <span className="emf-req">*</span>
                </label>
                <input
                  className={`emf-input ${errors.contact_number ? "emf-input--error" : ""}`}
                  type="text"
                  name="contact_number"
                  value={franchiseData.contact_number}
                  onChange={handleAddChange}
                  placeholder="e.g. 9876543211"
                  disabled={loading}
                />
                {errors.contact_number && <span className="emf-error-msg">{errors.contact_number}</span>}
              </div>

              <div className="emf-field emf-field--full">
                <label className="emf-label">
                  Profile Image <span className="emf-optional">(Optional)</span>
                </label>

                {logoPreview ? (
                  <div className="prv-icon-preview">
                    <div className="prv-icon-preview-img-wrap">
                      {uploadingLogo && (
                        <div className="prv-icon-uploading-overlay">
                          <svg className="prv-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                          </svg>
                          <span>Uploading...</span>
                        </div>
                      )}
                      <img src={logoPreview} alt="Profile preview" className="prv-icon-preview-img" />
                    </div>

                    <div className="prv-icon-preview-info">
                      <span className="prv-icon-preview-label">Profile image</span>
                      {franchiseData.profile_image && (
                        <span className="prv-icon-preview-url">{franchiseData.profile_image.slice(0, 48)}...</span>
                      )}

                      <div className="prv-icon-preview-actions">
                        <button
                          type="button"
                          className="prv-icon-change-btn"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo || loading}
                        >
                          Change
                        </button>

                        <button
                          type="button"
                          className="prv-icon-remove-btn"
                          onClick={removeLogo}
                          disabled={uploadingLogo || loading}
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleLogoFileChange}
                    />
                  </div>
                ) : (
                  <div
                    className={`prv-icon-dropzone ${logoDragOver ? "prv-icon-dropzone--over" : ""}`}
                    onClick={() => logoInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setLogoDragOver(true);
                    }}
                    onDragLeave={() => setLogoDragOver(false)}
                    onDrop={handleLogoDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && logoInputRef.current?.click()}
                  >
                    <div className="prv-icon-dropzone-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>

                    <div className="prv-icon-dropzone-text">
                      <span className="prv-icon-dropzone-title">
                        {logoDragOver ? "Drop image here" : "Upload profile image"}
                      </span>
                      <span className="prv-icon-dropzone-sub">
                        Click to browse or drag & drop · PNG, JPG, SVG, WebP
                      </span>
                    </div>

                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={handleLogoFileChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`emf-section ${activeSection === 1 ? "emf-section--visible" : ""}`}>
            <div className="emf-section-header">
              <span className="emf-section-badge">02</span>
              <h2 className="emf-section-title">Location & Access</h2>
            </div>

            <div className="emf-grid emf-grid-2">
              <div className="emf-field emf-field--full">
                <label className="emf-label">
                  Address <span className="emf-optional">(Optional)</span>
                </label>
                <input
                  className="emf-input"
                  type="text"
                  name="address"
                  value={franchiseData.address}
                  onChange={handleAddChange}
                  placeholder="Street, city, state..."
                  disabled={loading}
                />
              </div>

              <div className="emf-field">
                <label className="emf-label">
                  Country <span className="emf-req">*</span>
                </label>
                <select
                  className={`emf-select ${errors.country ? "emf-input--error" : ""}`}
                  name="country"
                  value={franchiseData.country}
                  onChange={handleAddChange}
                  disabled={loading}
                >
                  <option value="">Select country</option>
                  {countryList?.data?.map((country) => (
                    <option key={country._id} value={country._id}>
                      {country.country_name}
                    </option>
                  ))}
                </select>
                {errors.country && <span className="emf-error-msg">{errors.country}</span>}
              </div>

              <SearchableMultiSelect
                label="City"
                required
                placeholder={!franchiseData.country ? "Select country first" : "Search cities..."}
                disabled={!franchiseData.country || loading}
                options={cities.map((c) => ({
                  value: c._id,
                  label: c.city_name,
                  disabled: c.available === false && !c.assigned_to_me,
                }))}
                selected={franchiseData.city}
                onChange={handleCityChange}
                error={errors.city}
                emptyMessage="Select a country first"
              />

              <div className="emf-field-with-action">
                <button
                  type="button"
                  className="emf-select-all-btn"
                  onClick={() => {
                    const availableValues = localityOptions
                      .filter((l) => !l.disabled)
                      .map((l) => l.value);
                    const allAvailableSelected =
                      availableValues.length > 0 &&
                      availableValues.every((v) => franchiseData.locality.includes(v));
                    handleLocalityChange(allAvailableSelected ? [] : availableValues);
                  }}
                  disabled={
                    franchiseData.city.length === 0 ||
                    loading ||
                    localityOptions.filter((l) => !l.disabled).length === 0
                  }
                >
                  {(() => {
                    const availableValues = localityOptions
                      .filter((l) => !l.disabled)
                      .map((l) => l.value);
                    const allAvailableSelected =
                      availableValues.length > 0 &&
                      availableValues.every((v) => franchiseData.locality.includes(v));
                    return allAvailableSelected ? "Unselect All" : "Select All";
                  })()}
                </button>

                <SearchableMultiSelect
                  label="Locality"
                  required
                  placeholder={franchiseData.city.length === 0 ? "Select city first" : "Search localities..."}
                  disabled={franchiseData.city.length === 0 || loading}
                  options={localityOptions}
                  selected={franchiseData.locality}
                  onChange={handleLocalityChange}
                  error={errors.locality}
                  emptyMessage="No localities for the selected city"
                />
              </div>

              <div className="emf-field-with-action">
                <button
                  type="button"
                  className="emf-select-all-btn"
                  onClick={() => {
                    const availableValues = projectOptions
                      .filter((p) => !p.disabled)
                      .map((p) => p.value);
                    const allAvailableSelected =
                      availableValues.length > 0 &&
                      availableValues.every((v) => franchiseData.project.includes(v));
                    handleProjectChange(allAvailableSelected ? [] : availableValues);
                  }}
                  disabled={
                    franchiseData.locality.length === 0 ||
                    loading ||
                    projectOptions.filter((p) => !p.disabled).length === 0
                  }
                >
                  {(() => {
                    const availableValues = projectOptions
                      .filter((p) => !p.disabled)
                      .map((p) => p.value);
                    const allAvailableSelected =
                      availableValues.length > 0 &&
                      availableValues.every((v) => franchiseData.project.includes(v));
                    return allAvailableSelected ? "Unselect All" : "Select All";
                  })()}
                </button>

                <SearchableMultiSelect
                  label="Project"
                  required
                  placeholder={franchiseData.locality.length === 0 ? "Select locality first" : "Search projects..."}
                  disabled={franchiseData.locality.length === 0 || loading}
                  options={projectOptions}
                  selected={franchiseData.project}
                  onChange={handleProjectChange}
                  error={errors.project}
                  emptyMessage="No projects for the selected city/locality"
                />
              </div>
            </div>
          </div>

          <div className="emf-footer">
            <div className="emf-footer-nav">
              <button
                type="button"
                className="emf-btn emf-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0 || loading || uploadingLogo}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              {activeSection < sections.length - 1 ? (
                <button
                  type="button"
                  className="emf-btn emf-btn--primary"
                  onClick={handleNextSection}
                  disabled={loading || uploadingLogo}
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="emf-btn emf-btn--submit"
                  disabled={loading || uploadingLogo}
                >
                  {loading ? "Saving..." : "Update Master Franchise"}
                </button>
              )}
            </div>

            <Link to="/franchise" className="emf-btn emf-btn--cancel">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditMasterFranchise;